import React, { useState, useRef } from 'react';
import { User, Lock, AlertCircle, X, ShieldCheck } from 'lucide-react';
import Button from './Button';
import { authService } from '../../services';
import usePermissionStore from '../../stores/permissionStore';

/**
 * 身份升级弹窗
 * 当用户通过 Access Token 访问时，点击「我的」需要验证完整账号信息
 */
const IdentityUpgradeModal = ({ isOpen, onClose, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // 防重入锁（同步），见 docs/ASYNC_LOCK_PATTERN.md
  const loadingRef = useRef(false);
  const [error, setError] = useState('');
  
  const { loginFromApi } = usePermissionStore();

  if (!isOpen) return null;

  const handleVerify = async () => {
    if (!username.trim()) {
      setError('请输入用户名');
      return;
    }
    if (!password.trim()) {
      setError('请输入密码');
      return;
    }

    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const response = await authService.login(username, password, false);
      
      // 验证成功，升级身份
      await loginFromApi(response.user, response.access_token);
      
      // 成功回调
      onSuccess();
    } catch (err) {
      console.error('身份验证失败:', err);
      let errorMessage = '验证失败，请稍后重试';
      if (err.message) {
        if (err.message.includes('Invalid username or password') || 
            err.message.includes('Invalid credentials')) {
          errorMessage = '用户名或密码错误';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* 弹窗内容 */}
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="px-6 pt-8 pb-6">
          <div className="flex justify-between items-start mb-6">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center">
              <ShieldCheck size={24} className="text-blue-600" />
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 mb-2">身份验证</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            您当前处于访客模式，访问「个人中心」需要验证完整账号信息。
          </p>
          
          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
          
          <div className="space-y-4 mb-8">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">用户名</label>
              <div className="flex items-center bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3 focus-within:bg-white focus-within:border-blue-500 focus-within:shadow-sm transition-all">
                <User size={18} className="text-gray-400 mr-3" />
                <input 
                  type="text" 
                  placeholder="请输入用户名" 
                  className="bg-transparent flex-1 outline-none text-sm text-gray-900 placeholder-gray-300"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">密码</label>
              <div className="flex items-center bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3 focus-within:bg-white focus-within:border-blue-500 focus-within:shadow-sm transition-all">
                <Lock size={18} className="text-gray-400 mr-3" />
                <input 
                  type="password" 
                  placeholder="请输入密码" 
                  className="bg-transparent flex-1 outline-none text-sm text-gray-900 placeholder-gray-300"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                />
              </div>
            </div>
          </div>
          
          <Button 
            onClick={handleVerify}
            loading={loading}
            className="w-full h-12 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-100 transition-all active:scale-[0.98]"
          >
            立即验证
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IdentityUpgradeModal;
